import { ApiError, apiFetch, apiRequest, apiUpload } from './client';
export async function getConsultationWorklist(params) {
    const searchParams = new URLSearchParams();
    searchParams.set('tab', params.tab);
    searchParams.set('mode', params.mode);
    if (params.page)
        searchParams.set('page', String(params.page));
    if (params.pageSize)
        searchParams.set('pageSize', String(params.pageSize));
    if (params.sortBy)
        searchParams.set('sortBy', params.sortBy);
    if (params.sortDir)
        searchParams.set('sortDir', params.sortDir);
    const f = params.filters;
    if (f.groupId)
        searchParams.set('groupId', f.groupId);
    if (f.clinicId)
        searchParams.set('clinicId', f.clinicId);
    if (f.status.length > 0)
        searchParams.set('status', f.status.join(','));
    if (f.priority.length > 0)
        searchParams.set('priority', f.priority.join(','));
    if (f.modality.length > 0)
        searchParams.set('modality', f.modality.join(','));
    if (f.species.length > 0)
        searchParams.set('species', f.species.join(','));
    if (f.bodyArea.length > 0)
        searchParams.set('bodyArea', f.bodyArea.join(','));
    if (f.assignedTo && f.assignedTo !== 'all')
        searchParams.set('assignedTo', f.assignedTo);
    if (f.search)
        searchParams.set('search', f.search);
    if (f.reportKeyword)
        searchParams.set('reportKeyword', f.reportKeyword);
    if (f.reportSection && f.reportSection !== 'all')
        searchParams.set('reportSection', f.reportSection);
    if (f.tag)
        searchParams.set('tag', f.tag);
    if (f.regionId)
        searchParams.set('regionId', f.regionId);
    if (f.unreadOnly)
        searchParams.set('unreadOnly', 'true');
    if (f.priorityStatUrgent)
        searchParams.set('priorityStatUrgent', 'true');
    if (f.favoritesOnly)
        searchParams.set('favoritesOnly', 'true');
    if (f.linkOnly)
        searchParams.set('linkOnly', 'true');
    const result = await apiFetch(`/teleconsultation/worklist?${searchParams.toString()}`);
    // Dev bridge (no-op in production): overlay submitterTier onto cases
    // submitted from the Link shell so the glyph and Link-only filter work
    // before the backend stamps the field — and, while it can't filter
    // server-side, apply linkOnly to the returned page here. In production
    // the param is server-side only: an unstamped backend simply ignores it
    // rather than this belt wrongly emptying the queue.
    if (process.env.NODE_ENV !== 'production') {
        const { enrichCasesWithLinkOrigin } = await import('./link-dev-origin');
        let cases = enrichCasesWithLinkOrigin(result.cases);
        if (f.linkOnly) {
            cases = cases.filter((c) => c.submitterTier === 'link');
            // The belt shrank the page, so the server's total would overstate the
            // footer and keep viewer-navigation prefetch chasing pages that don't
            // exist — patch it to what's actually shown.
            return { ...result, cases, total: cases.length };
        }
        return { ...result, cases };
    }
    return result;
}
export async function favoriteConsultationCase(caseId) {
    await apiRequest('POST', `/teleconsultation/cases/${caseId}/favorite`);
}
export async function unfavoriteConsultationCase(caseId) {
    await apiRequest('DELETE', `/teleconsultation/cases/${caseId}/favorite`);
}
/**
 * The private, receiving-group-internal comment thread on a case. The server
 * refuses non-receivers (including the submitting clinic) with a 403, so
 * callers on mixed-audience surfaces should treat an ApiError as "hide the
 * panel", not a failure.
 */
export async function getCaseComments(caseId) {
    return await apiFetch(`/teleconsultation/cases/${caseId}/comments`);
}
export async function addCaseComment(caseId, body) {
    return await apiRequest('POST', `/teleconsultation/cases/${caseId}/comments`, {
        body,
    });
}
export async function deleteCaseComment(caseId, commentId) {
    await apiRequest('DELETE', `/teleconsultation/cases/${caseId}/comments/${commentId}`);
}
export async function claimConsultationCase(caseId) {
    await apiRequest('POST', `/teleconsultation/cases/${caseId}/claim`);
}
export async function unclaimConsultationCase(caseId) {
    await apiRequest('POST', `/teleconsultation/cases/${caseId}/unclaim`);
}
export async function reassignConsultationCase(caseId, targetRadiologistId) {
    await apiRequest('POST', `/teleconsultation/cases/${caseId}/reassign`, {
        radiologistId: targetRadiologistId,
    });
}
export async function changeCasePriority(caseId, priority) {
    await apiRequest('POST', `/teleconsultation/cases/${caseId}/priority`, { priority });
}
export async function submitConsultation(data) {
    // SubmitCase returns { id } of the created case; surface it so callers
    // don't have to re-derive it via the by-study lookup (which races and
    // answers "newest case for this study", not "the case this POST made").
    return await apiRequest('POST', '/teleconsultation/cases', data);
}
/**
 * GET /teleconsultation/groups/{groupId}/request-templates — the group's
 * teleconsultation templates the caller may submit against, name ASC. Readable
 * by members of a connected clinic, the group's own members, and super admins.
 * Array members are defensively defaulted: the backend omits null-valued field
 * properties entirely and older rows can carry SQL nulls for the text[] columns.
 */
export async function getGroupRequestTemplates(groupId) {
    const result = await apiFetch(`/teleconsultation/groups/${groupId}/request-templates`);
    return (result.templates ?? []).map((template) => ({
        ...template,
        modalities: template.modalities ?? [],
        bodyAreas: template.bodyAreas ?? [],
        species: template.species ?? [],
        requestFields: template.requestFields ?? [],
    }));
}
export async function getConsultantProfile() {
    const profile = await apiFetch('/teleconsultation/profile');
    return {
        ...profile,
        role: String(profile.role).toLowerCase(),
        groups: (profile.groups ?? []).map((g) => ({
            ...g,
            role: String(g.role).toLowerCase(),
        })),
    };
}
export async function getConsultantGroups() {
    return apiFetch('/teleconsultation/groups');
}
export async function getAvailableGroups(clinicId) {
    return apiFetch(`/teleconsultation/available-groups?clinicId=${clinicId}`);
}
export async function getGroupPriorityConfig(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}/priority-config`);
}
export async function updateGroupPriorityConfig(groupId, priorities) {
    return apiFetch(`/teleconsultation/groups/${groupId}/priority-config`, {
        method: 'PUT',
        body: JSON.stringify({ priorities }),
    });
}
export async function getGroupMembers(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}/members`);
}
export async function getTcGroups() {
    return apiFetch('/teleconsultation/groups');
}
export async function getTcGroupDetail(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}`);
}
export async function createTcGroup(data) {
    return apiRequest('POST', '/teleconsultation/groups', data);
}
export async function updateTcGroup(groupId, data) {
    return apiRequest('PATCH', `/teleconsultation/groups/${groupId}`, data);
}
/**
 * Retires a group: deactivates it, disables every clinic connection and every
 * membership. Cases and reports survive. 409 while consultations are still open.
 */
export async function deleteTcGroup(groupId) {
    await apiRequest('DELETE', `/teleconsultation/groups/${groupId}`);
}
export async function getConnectedClinics(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}/clinics`);
}
export async function connectClinicToGroup(groupId, data) {
    await apiRequest('POST', `/teleconsultation/groups/${groupId}/clinics`, data);
}
export async function updateClinicConnection(groupId, clinicId, data) {
    // Wire contract: the backend only clears the connection default via the
    // `clearDefaultTemplate: true` sentinel — a null defaultTemplateId is
    // ignored (COALESCE keeps the stored value). Translate the ergonomic
    // `defaultTemplateId: null` here and never send null on the wire.
    // pdfLayoutId mirrors this exactly with its `clearPdfLayout: true` sentinel.
    const { defaultTemplateId, pdfLayoutId, ...rest } = data;
    const body = { ...rest };
    if (defaultTemplateId === null)
        body.clearDefaultTemplate = true;
    else if (defaultTemplateId !== undefined)
        body.defaultTemplateId = defaultTemplateId;
    if (pdfLayoutId === null)
        body.clearPdfLayout = true;
    else if (pdfLayoutId !== undefined)
        body.pdfLayoutId = pdfLayoutId;
    await apiRequest('PATCH', `/teleconsultation/groups/${groupId}/clinics/${clinicId}`, body);
}
/**
 * PATCH /teleconsultation/clinics/{clinicId}/groups/{groupId}/preferences —
 * the clinic-admin counterpart of updateClinicConnection (each side edits its
 * own half of the connection). Omitted fields are left unchanged.
 */
export async function patchConnectionPreferences(clinicId, groupId, prefs) {
    await apiRequest('PATCH', `/teleconsultation/clinics/${clinicId}/groups/${groupId}/preferences`, prefs);
}
export async function disconnectClinicFromGroup(groupId, clinicId) {
    await apiRequest('DELETE', `/teleconsultation/groups/${groupId}/clinics/${clinicId}`);
}
// ——— Group branding (TC report PDFs carry the receiving group's letterhead) ———
export async function uploadTcGroupLogo(groupId, file) {
    const formData = new FormData();
    formData.append('file', file);
    // PUT, not POST: there is exactly one logo per group, so the write is an
    // idempotent whole-resource replace. Same endpoint the Link branding surface
    // calls (uploadGroupLogo in lib/api/link.ts).
    return apiUpload('PUT', `/teleconsultation/groups/${groupId}/logo`, formData);
}
export async function removeTcGroupLogo(groupId) {
    await apiRequest('DELETE', `/teleconsultation/groups/${groupId}/logo`);
}
export async function updateTcGroupBrandingText(groupId, body) {
    return apiRequest('PUT', `/teleconsultation/groups/${groupId}/branding-text`, body);
}
export async function getTcGroupReportDefaults(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}/report-defaults`);
}
/** Replaces the group's full defaults set (PUT semantics). The backend binds a BARE array body. */
export async function updateTcGroupReportDefaults(groupId, defaults) {
    return apiFetch(`/teleconsultation/groups/${groupId}/report-defaults`, {
        method: 'PUT',
        body: JSON.stringify(defaults),
    });
}
export async function getTcGroupMembers(groupId) {
    const members = await apiFetch(`/teleconsultation/groups/${groupId}/members`);
    return members.map((m) => ({ ...m, role: m.role.toLowerCase() }));
}
export async function updateTcGroupMemberRole(groupId, memberId, role) {
    return apiRequest('PATCH', `/teleconsultation/groups/${groupId}/members/${memberId}`, { role });
}
export async function removeTcGroupMember(groupId, memberId) {
    await apiRequest('DELETE', `/teleconsultation/groups/${groupId}/members/${memberId}`);
}
export async function submitConsultationAmendment(data) {
    await apiRequest('POST', `/teleconsultation/cases/${data.caseId}/amend`, data);
}
export async function cancelConsultationCase(caseId) {
    await apiRequest('POST', `/teleconsultation/cases/${caseId}/cancel`);
}
/**
 * Soft-delete a consultation request. Group admins of the case's assigned
 * group, clinic admins of the submitting clinic, and super admins only —
 * any state (the UI carries a stronger confirm for finalized cases).
 */
export async function deleteConsultationCase(caseId, reason) {
    // The body is optional on the endpoint; omit it entirely when no reason was
    // given rather than sending an empty string the server would have to strip.
    await apiRequest('DELETE', `/teleconsultation/cases/${caseId}`, reason ? { reason } : undefined);
}
export async function getGroupNotificationConfig(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}/notification-config`);
}
export async function updateGroupNotificationConfig(groupId, payload) {
    return apiRequest('PUT', `/teleconsultation/groups/${groupId}/notification-config`, payload);
}
export async function getConsultationForStudy(studyId) {
    const result = await apiFetch(`/teleconsultation/cases/by-study?studyId=${encodeURIComponent(studyId)}`);
    return result && 'id' in result ? result : null;
}
export async function getConsultationCase(caseId) {
    return apiFetch(`/teleconsultation/cases/${caseId}`);
}
/** Claim and finalize history for a case. Receiving-group-internal. */
export async function getCaseActivity(caseId) {
    return apiFetch(`/teleconsultation/cases/${caseId}/activity`);
}
export async function getResidentReports(caseId) {
    return apiFetch(`/teleconsultation/cases/${caseId}/resident-reports`);
}
export async function getMyResidentReport(caseId) {
    try {
        return await apiFetch(`/teleconsultation/cases/${caseId}/resident-report/me`);
    }
    catch (error) {
        // 404 = no draft yet (or workflow disabled for this case); treat as "no report".
        if (error instanceof ApiError && error.status === 404)
            return null;
        throw error;
    }
}
export async function saveMyResidentReport(caseId, content, images) {
    return apiRequest('PUT', `/teleconsultation/cases/${caseId}/resident-report/me/save`, {
        content,
        images,
    });
}
export async function submitMyResidentReport(caseId) {
    return apiRequest('POST', `/teleconsultation/cases/${caseId}/resident-report/me/submit`);
}
export async function saveResidentReportReview(caseId, residentUserId, content, images) {
    return apiRequest('PUT', `/teleconsultation/cases/${caseId}/resident-reports/${residentUserId}/review`, { content, images });
}
export async function approveResidentReport(caseId, residentUserId) {
    return apiRequest('POST', `/teleconsultation/cases/${caseId}/resident-reports/${residentUserId}/approve`);
}
// ——— Region routing ———
export async function getRegions(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}/regions`);
}
export async function createRegion(groupId, data) {
    return apiRequest('POST', `/teleconsultation/groups/${groupId}/regions`, data);
}
export async function updateRegion(groupId, regionId, data) {
    return apiRequest('PATCH', `/teleconsultation/groups/${groupId}/regions/${regionId}`, data);
}
export async function deleteRegion(groupId, regionId) {
    await apiRequest('DELETE', `/teleconsultation/groups/${groupId}/regions/${regionId}`);
}
export async function getClinicRegionAssignments(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}/clinic-region-assignments`);
}
export async function assignClinicToRegion(groupId, payload) {
    if (payload.regionId === null) {
        await apiRequest('DELETE', `/teleconsultation/groups/${groupId}/clinic-region-assignments/${payload.clinicId}`);
    }
    else {
        await apiRequest('PUT', `/teleconsultation/groups/${groupId}/clinic-region-assignments`, {
            clinicId: payload.clinicId,
            regionId: payload.regionId,
        });
    }
}
export async function bulkAssignClinicsToRegion(groupId, payload) {
    if (payload.regionId === null) {
        await apiRequest('POST', `/teleconsultation/groups/${groupId}/clinic-region-assignments/bulk-unassign`, { clinicIds: payload.clinicIds });
    }
    else {
        await apiRequest('POST', `/teleconsultation/groups/${groupId}/clinic-region-assignments/bulk`, payload);
    }
}
export async function getRadiologistRegionAssignments(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}/radiologist-region-assignments`);
}
export async function assignRadiologistToRegion(groupId, payload) {
    await apiRequest('POST', `/teleconsultation/groups/${groupId}/radiologist-region-assignments`, {
        memberId: payload.radiologistId,
        regionId: payload.regionId,
    });
}
export async function removeRadiologistFromRegion(groupId, assignmentId) {
    await apiRequest('DELETE', `/teleconsultation/groups/${groupId}/radiologist-region-assignments/${assignmentId}`);
}
export async function getTierConfig(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}/tier-config`);
}
export async function updateTierConfig(groupId, data) {
    return apiRequest('PATCH', `/teleconsultation/groups/${groupId}/tier-config`, data);
}
export async function getTierReleaseLog(groupId, limit) {
    const q = limit != null ? `?limit=${limit}` : '';
    return apiFetch(`/teleconsultation/groups/${groupId}/tier-release-log${q}`);
}
export async function getTcBillingUsageSummary(params) {
    const sp = new URLSearchParams();
    sp.set('from', params.from);
    sp.set('to', params.to);
    sp.set('bucket', params.bucket);
    sp.set('scope', params.scope ?? 'self');
    sp.set('format', 'json');
    if (params.groupId)
        sp.set('groupId', params.groupId);
    return apiFetch(`/teleconsultation/billing-usage/summary?${sp.toString()}`);
}
// ——— Billing code catalog (CRUD) ———
export async function listGroupBillingCodes(groupId, active = 'true') {
    const result = await apiFetch(`/teleconsultation/groups/${groupId}/billing-codes?active=${active}`);
    return result.codes;
}
/**
 * The catalog the report editor's picker offers for ONE report: the active codes of the
 * case's assigned consultant group, resolved server-side from the report (so the picker and
 * PUT /billing can never disagree about which catalog applies). `groupId` is null when the
 * case never landed in a group, in which case there is nothing to bill against.
 */
export async function listReportBillingCodes(reportId) {
    const result = await apiFetch(`/teleconsultation/reports/${reportId}/billing-codes`);
    return { groupId: result.groupId ?? null, codes: result.codes ?? [] };
}
export async function createGroupBillingCode(groupId, input) {
    return apiRequest('POST', `/teleconsultation/groups/${groupId}/billing-codes`, input);
}
export async function patchGroupBillingCode(groupId, codeId, patch) {
    return apiRequest('PATCH', `/teleconsultation/groups/${groupId}/billing-codes/${codeId}`, patch);
}
export async function deleteGroupBillingCode(groupId, codeId) {
    await apiRequest('DELETE', `/teleconsultation/groups/${groupId}/billing-codes/${codeId}`);
}
// ——— Report billing write ———
export async function putReportBilling(reportId, sites) {
    return apiRequest('PUT', `/teleconsultation/reports/${reportId}/billing`, {
        billingSites: sites,
    });
}
// ——— Custom saved worklists (3.11) ———
export async function getSavedWorklists() {
    return apiFetch('/teleconsultation/saved-worklists');
}
export async function createSavedWorklist(data) {
    return apiRequest('POST', '/teleconsultation/saved-worklists', data);
}
export async function updateSavedWorklist(id, data) {
    return apiRequest('PATCH', `/teleconsultation/saved-worklists/${id}`, data);
}
export async function deleteSavedWorklist(id) {
    await apiRequest('DELETE', `/teleconsultation/saved-worklists/${id}`);
}
// ——— Algorithmic routing config + per-radiologist tuning (3.11) ———
export async function getRoutingConfig(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}/routing-config`);
}
export async function updateRoutingConfig(groupId, data) {
    return apiRequest('PATCH', `/teleconsultation/groups/${groupId}/routing-config`, data);
}
export async function upsertRoutingWeight(groupId, memberId, data) {
    await apiRequest('PUT', `/teleconsultation/groups/${groupId}/routing-weights/${memberId}`, data);
}
// ——— Configurable routing rules (3.06) ———
export async function getRoutingRules(groupId) {
    return apiFetch(`/teleconsultation/groups/${groupId}/routing-rules`);
}
export async function createRoutingRule(groupId, input) {
    return apiRequest('POST', `/teleconsultation/groups/${groupId}/routing-rules`, input);
}
export async function updateRoutingRule(groupId, ruleId, input) {
    return apiRequest('PATCH', `/teleconsultation/groups/${groupId}/routing-rules/${ruleId}`, input);
}
export async function deleteRoutingRule(groupId, ruleId) {
    await apiRequest('DELETE', `/teleconsultation/groups/${groupId}/routing-rules/${ruleId}`);
}
export async function reorderRoutingRules(groupId, orderedIds) {
    await apiRequest('POST', `/teleconsultation/groups/${groupId}/routing-rules/reorder`, {
        orderedIds,
    });
}
export async function applyRoutingRulesToPending(groupId) {
    return apiRequest('POST', `/teleconsultation/groups/${groupId}/routing-rules/apply-pending`);
}
export async function setMemberAvailability(groupId, memberId, isAvailable) {
    await apiRequest('PUT', `/teleconsultation/groups/${groupId}/routing-availability/${memberId}`, {
        isAvailable,
    });
}
// ——— Visit bundling (3.10) ———
export async function getBundleCandidates(caseId) {
    return apiFetch(`/teleconsultation/bundle-candidates?caseId=${encodeURIComponent(caseId)}`);
}
export async function getBundleCandidatesForStudy(studyId) {
    return apiFetch(`/teleconsultation/bundle-candidates?studyId=${encodeURIComponent(studyId)}`);
}
export async function getBundle(bundleId) {
    return apiFetch(`/teleconsultation/bundles/${bundleId}`);
}
export async function createBundle(data) {
    return apiRequest('POST', '/teleconsultation/bundles', data);
}
export async function addBundleCase(bundleId, caseId) {
    return apiRequest('POST', `/teleconsultation/bundles/${bundleId}/cases`, { caseId });
}
export async function removeBundleCase(bundleId, caseId) {
    return apiRequest('DELETE', `/teleconsultation/bundles/${bundleId}/cases/${caseId}`);
}
export async function deleteBundle(bundleId) {
    await apiRequest('DELETE', `/teleconsultation/bundles/${bundleId}`);
}
